/**
 * Utility to auto-format plain text responses into markdown-like structure
 * Detects patterns like numbered steps, bullet points, and key phrases
 */

export function formatPlainTextToMarkdown(text: string): string {
  if (!text) return text;

  let formatted = text;

  // First, normalize line breaks
  formatted = formatted.trim();

  // Detect pattern: "To accomplish this task, you need to follow these steps: 1. ... 2. ... 3. ..."
  // Split into separate lines
  formatted = formatted.replace(
    /(\d+)\.\s+([A-Z][^.]*\.)/g,
    (match, num, content) => `\n${num}. ${content}`
  );

  // Detect sentences that start with "Based on" and make them separate paragraphs
  formatted = formatted.replace(
    /(Based on [^.]+\.)/g,
    "\n\n$1\n\n"
  );

  // Detect introductory phrases like "To accomplish this task, you need to follow these steps:"
  formatted = formatted.replace(
    /^(To accomplish this task[^:]+:)\s*/im,
    "**$1**\n\n"
  );

  // Detect "here's the relevant information" and similar phrases
  formatted = formatted.replace(
    /(here'?s the relevant information|here is the relevant information)/gi,
    "**$1**"
  );

  // Detect and format numbered steps that might be inline
  // Pattern: "1. Do this 2. Do that 3. Do something"
  const numberedStepPattern = /\b(\d+)\.\s+([A-Z][^.]*\.)(?:\s+(\d+)\.\s+)?/g;
  let hasNumberedSteps = numberedStepPattern.test(formatted);
  
  if (hasNumberedSteps) {
    formatted = formatted.replace(
      /\b(\d+)\.\s+([^.]+\.)/g,
      (match, num, content, offset, string) => {
        // Check if it's already on its own line
        const beforeChar = offset > 0 ? string[offset - 1] : '\n';
        if (beforeChar === '\n') {
          return match; // Already formatted
        }
        return `\n${num}. ${content.trim()}`;
      }
    );
  }

  // Detect action verbs at sentence start and convert to bullet points
  const actionVerbs = [
    "Navigate", "Select", "Configure", "Save", "Click", "Open", "Access",
    "Choose", "Enter", "Set", "Enable", "Disable", "Create", "Delete",
    "Update", "Modify", "Review", "Check", "Verify", "Confirm", "Manage",
    "Organize", "Initialize", "Search", "Customize"
  ];
  
  const actionVerbPattern = new RegExp(
    `^(${actionVerbs.join("|")})\\s+([^.]+\\.)`,
    "gim"
  );
  
  formatted = formatted.replace(actionVerbPattern, (match, verb, content) => {
    // Don't convert if already in a numbered list
    if (match.trim().match(/^\d+\./)) {
      return match;
    }
    return `- ${verb} ${content}`;
  });

  // Bold section headers (sentences ending with ":" at line start)
  formatted = formatted.replace(
    /^([^:\n]{10,80}):(?=\s*$)/gm,
    "**$1:**"
  );

  // Bold common emphatic phrases
  const emphasisPhrases = [
    "Important", "Note", "Warning", "Tip", "Remember",
    "The system", "This feature", "You can"
  ];
  
  emphasisPhrases.forEach(phrase => {
    const regex = new RegExp(`\\b(${phrase})\\b(?=[^*])`, "gi");
    formatted = formatted.replace(regex, (match) => {
      // Check if already bold
      return `**${match}**`;
    });
  });

  // Clean up: remove excessive newlines (more than 2 consecutive)
  formatted = formatted.replace(/\n{3,}/g, "\n\n");
  
  // Clean up: trim spaces at line ends
  formatted = formatted.replace(/[ \t]+$/gm, "");

  // Ensure proper spacing after list items
  formatted = formatted.replace(/^(\d+\.\s+[^\n]+)(\n)(?=\d+\.)/gm, "$1\n");
  formatted = formatted.replace(/^(-\s+[^\n]+)(\n)(?=-\s+)/gm, "$1\n");

  return formatted.trim();
}

/**
 * Windows-1252 / CP-1252 bytes 0x80–0x9F are sometimes transmitted as their
 * Latin-1 "equivalent" Unicode code points (U+0080–U+009F).  Those code points
 * belong to the C1 control-character range in Unicode and are non-printable,
 * causing browsers and PDF renderers to display them as "?".
 *
 * This table maps each such code point to the correct Unicode character that
 * Windows-1252 actually intended at that byte position.
 */
const WIN1252_TO_UNICODE: Partial<Record<number, string>> = {
  0x80: "\u20AC", // €
  0x82: "\u201A", // ‚  (single low-9 quotation mark – looks like a comma)
  0x83: "\u0192", // ƒ
  0x84: "\u201E", // „  (double low-9 quotation mark)
  0x85: "\u2026", // …  (ellipsis)
  0x86: "\u2020", // †
  0x87: "\u2021", // ‡
  0x88: "\u02C6", // ˆ
  0x89: "\u2030", // ‰
  0x8a: "\u0160", // Š
  0x8b: "\u2039", // ‹
  0x8c: "\u0152", // Œ
  0x8e: "\u017D", // Ž
  0x91: "\u2018", // '  (left single quotation mark)
  0x92: "\u2019", // '  (right single quotation mark / smart apostrophe)
  0x93: "\u201C", // "  (left double quotation mark)
  0x94: "\u201D", // "  (right double quotation mark)
  0x95: "\u2022", // •
  0x96: "\u2013", // –  (en dash)
  0x97: "\u2014", // —  (em dash)
  0x98: "\u02DC", // ˜
  0x99: "\u2122", // ™
  0x9a: "\u0161", // š
  0x9b: "\u203A", // ›
  0x9c: "\u0153", // œ
  0x9e: "\u017E", // ž
  0x9f: "\u0178", // Ÿ
};

/**
 * Normalises text encoding by:
 * 1. Mapping any Windows-1252 code points that ended up in the C1 Unicode
 *    control-character range (U+0080–U+009F) to their correct Unicode
 *    equivalents.  This fixes smart quotes, dashes and similar punctuation
 *    that AI backends sometimes transmit using CP-1252 byte values.
 * 2. Stripping bare U+FFFD replacement characters that result from genuinely
 *    invalid byte sequences in the source stream.
 */
export function normalizeTextEncoding(text: string): string {
  if (!text) return text;
  return text.replace(/[\u0080-\u009F\uFFFD]/g, (char) => {
    if (char === "\uFFFD") return ""; // drop unrecoverable bytes
    return WIN1252_TO_UNICODE[char.charCodeAt(0)] ?? char;
  });
}

/**
 * Recursively walks an arbitrary value and applies `normalizeTextEncoding` to
 * every string it encounters.  Safe to call on objects, arrays, and primitives.
 */
export function deepNormalizeStrings(value: unknown): unknown {
  if (typeof value === "string") return normalizeTextEncoding(value);
  if (Array.isArray(value)) return value.map(deepNormalizeStrings);
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as object)) {
      out[k] = deepNormalizeStrings((value as Record<string, unknown>)[k]);
    }
    return out;
  }
  return value;
}

/**
 * Detect if text is already in markdown format
 */
export function isMarkdownFormatted(text: string): boolean {
  const markdownPatterns = [
    /^#{1,6}\s+/m,           // Headers
    /\*\*[^*]+\*\*/,          // Bold
    /\*[^*]+\*/,              // Italic  
    /^\s*[-*+]\s+/m,         // Unordered lists
    /^\s*\d+\.\s+/m,         // Ordered lists
    /```[\s\S]*```/,          // Code blocks
    /`[^`]+`/,                // Inline code
    /^\s*>\s+/m,              // Blockquotes
    /\[([^\]]+)\]\(([^)]+)\)/ // Links
  ];

  // Count how many markdown patterns are present
  const matchCount = markdownPatterns.filter(pattern => pattern.test(text)).length;
  
  // Consider it markdown if it has 2 or more markdown patterns
  return matchCount >= 2;
}
